// Workout calorie ESTIMATE (HC-5). Pure functions, no zod, no IO — callable
// from any layer (mobile save-path, future MCP, web) to compute JL's OWN energy
// estimate for a workout, so a calorie figure exists even when Health Connect
// reports none. Modelled on navy.ts: metric-canonical units, formula documented
// with its source + honesty caveat, `null` on missing/invalid input.
//
// Two routes, picked by what data the bout carries:
//
//   • KEYTEL (HR-based) when a measured average HR exists — the athlete's real
//     intensity, not a table guess. Keytel et al. (2005), Journal of Sports
//     Sciences: kcal/min from HR + weight + age + sex. Validated on STEADY-STATE
//     AEROBIC exercise, so it is only trustworthy (high confidence) for cardio;
//     applied to intermittent strength work it is off-domain — we still use the
//     number (HR is real) but flag it low confidence.
//   • MET fallback when there's no HR: kcal = MET × 3.5 × kg / 200 × min (the
//     Compendium identity, VO2 = MET·3.5 mL/kg/min → kcal). A table assumption,
//     never a measurement, so always low confidence. Cardio METs are decent;
//     strength METs are coarse and honestly approximate.
//
// Units: weight in kilograms (app-wide canonical, units.ts), duration in
// minutes. The caller resolves those + the athlete's demographics; this module
// only does the physiology.

import type { AgeBucket, Profile, Sex } from "./schemas/profile.js";

// Single source for the two closed unions, so the Session schema's persisted
// `caloriesEstimate` (schemas/session.ts) validates EXACTLY what the estimator
// can produce — add a method/confidence here and the zod enum follows, instead
// of a hand-mirrored literal silently dropping the new value at save. Same
// tuple-derived-enum pattern profile.ts uses for its shared enums.
export const CALORIE_METHODS = ["keytel", "met"] as const;
export const CALORIE_CONFIDENCES = ["high", "low"] as const;
export type CalorieMethod = (typeof CALORIE_METHODS)[number];
export type CalorieConfidence = (typeof CALORIE_CONFIDENCES)[number];

export type CalorieEstimate = {
  kcal: number;
  method: CalorieMethod;
  // `high` only for a Keytel estimate on a CARDIO bout (measured HR in Keytel's
  // validated aerobic domain). Everything else — MET table, or Keytel on
  // strength — is `low`, so the UI + coach can caveat instead of trusting a
  // coarse number.
  confidence: CalorieConfidence;
};

// The MET bucket a movement falls in. Cardio carries a per-modality MET resolved
// from the slug; strength is priced per-set by the rest-aware window model below
// (not a bucket), so `kind` survives only to distinguish the two for the
// confidence verdict in estimateBoutCalories.
export type MetKind = "strength" | "cardio";
export type MetCategory = { kind: MetKind; met: number };

// Per-modality cardio METs for the taxonomy's known cardio slugs (the 9 rows
// with movementPattern "cardio" in muscle-taxonomy.json). Unknown/new cardio
// falls back to a moderate-vigorous default rather than a strength MET.
const CARDIO_MET_BY_SLUG: Record<string, number> = {
  running: 9.8,
  cycling: 7.5,
  walking: 3.5,
  "rowing-machine": 7.0,
  "stair-climber": 8.8,
  "cross-trainer-elliptical": 5.0,
  "jump-rope": 11.0,
  "battle-ropes": 8.0,
  "mountain-climbers": 8.0,
};
const CARDIO_MET_DEFAULT = 7.0;

/**
 * The MET category for a CARDIO bout, resolved from its slug (unknown/new cardio
 * falls back to a moderate-vigorous default). Strength no longer routes through
 * here — since HC-CAL it is priced per-set by the rest-aware window model
 * (strengthSetWindow, below), so a single cardio-only classifier is all the
 * estimator needs.
 */
export const cardioMetCategory = (slug: string): MetCategory => ({
  kind: "cardio",
  met: CARDIO_MET_BY_SLUG[slug] ?? CARDIO_MET_DEFAULT,
});

// Midpoint age for each 5-year bucket (Profile stores only AgeBucket, never a
// DOB — profile.ts). Keytel needs a number; the bucket midpoint is close enough
// for a calorie estimate. Exported: HC-7's %HRmax (220 − age) is the next
// caller. `70-plus` has no upper edge — 74 is a reasonable representative.
const AGE_BUCKET_MIDPOINT: Record<AgeBucket, number> = {
  under_18: 16,
  "18-24": 21,
  "25-29": 27,
  "30-34": 32,
  "35-39": 37,
  "40-44": 42,
  "45-49": 47,
  "50-54": 52,
  "55-59": 57,
  "60-64": 62,
  "65-69": 67,
  "70-plus": 74,
};

export const ageFromBucket = (bucket: AgeBucket | undefined): number | undefined =>
  bucket ? AGE_BUCKET_MIDPOINT[bucket] : undefined;

// Absurd inputs (a garbage 250-bpm HR over a long bout) can produce a nonsense
// figure; clamp to a sane open band and round to whole kcal so no consumer ever
// shows a fractional or wild calorie. Returns null for a non-finite result.
const cleanKcal = (kcal: number): number | null => {
  if (!Number.isFinite(kcal)) return null;
  return Math.round(Math.min(10000, Math.max(0, kcal)));
};

/**
 * MET-route kcal: MET × 3.5 × kg / 200 × minutes. Returns null unless MET,
 * weight, and duration are all strictly positive.
 */
export const metKcal = (
  met: number,
  weightKg: number,
  durationMin: number,
): number | null => {
  if (!(met > 0) || !(weightKg > 0) || !(durationMin > 0)) return null;
  return cleanKcal(((met * 3.5 * weightKg) / 200) * durationMin);
};

/**
 * Keytel-route kcal from average HR. Returns null when any required input is
 * missing/non-positive, or when the linear model yields a non-positive per-
 * minute rate (very low HR relative to weight/age can drive it negative — that
 * means "no meaningful expenditure measured", not a real burn).
 */
export const keytelKcal = (
  avgHrBpm: number,
  weightKg: number,
  ageYears: number,
  sex: Sex,
  durationMin: number,
): number | null => {
  if (
    !(avgHrBpm > 0) ||
    !(weightKg > 0) ||
    !(ageYears > 0) ||
    !(durationMin > 0)
  ) {
    return null;
  }
  const perMin =
    sex === "male"
      ? (-55.0969 + 0.6309 * avgHrBpm + 0.1988 * weightKg + 0.2017 * ageYears) /
        4.184
      : (-20.4022 + 0.4472 * avgHrBpm - 0.1263 * weightKg + 0.074 * ageYears) /
        4.184;
  if (!(perMin > 0)) return null;
  return cleanKcal(perMin * durationMin);
};

// One activity bout to estimate. `category` is the atomic {kind, met} (from
// cardioMetCategory or strengthSetWindow) — carried as a unit (not a loose met +
// isCardio pair) so a
// caller can never supply a MET from one movement and a kind from another and
// silently corrupt the confidence verdict. avgHrBpm is the bout's measured mean
// HR when present.
export type CalorieBout = {
  durationMin: number;
  category: MetCategory;
  avgHrBpm?: number;
};

// The athlete's energy demographics, resolved by the caller from Profile.
export type AthleteEnergy = {
  weightKg?: number;
  ageYears?: number;
  sex?: Sex;
};

/**
 * Map a Profile to the estimator's AthleteEnergy. `weightKg` is the profile's
 * current bodyweight scalar (units.ts canonical kg) — persist-at-save captures
 * it as the weight AT the workout, which a read-time recompute could not
 * reconstruct (HC-5 §persist-at-save). `ageYears` is the 5-year bucket
 * midpoint. Missing fields stay undefined, and estimateBoutCalories degrades
 * honestly (MET route, or null if even the weight is absent). One mapper so no
 * caller re-inlines the Profile→{weight,age,sex} destructure.
 */
export const resolveAthleteEnergy = (
  profile: Pick<Profile, "bodyweightKg" | "sex" | "ageBucket">,
): AthleteEnergy => ({
  weightKg: profile.bodyweightKg,
  ageYears: ageFromBucket(profile.ageBucket),
  sex: profile.sex,
});

/**
 * Estimate one bout's calories, choosing the best available route. Keytel when
 * a measured HR + the demographics it needs are present (high confidence only
 * on cardio); otherwise the MET fallback. Returns null when even the fallback
 * lacks its inputs (no weight or no duration) — an honest "can't estimate"
 * rather than a fabricated number.
 */
export const estimateBoutCalories = (
  bout: CalorieBout,
  athlete: AthleteEnergy,
): CalorieEstimate | null => {
  const { durationMin, category, avgHrBpm } = bout;
  const { weightKg, ageYears, sex } = athlete;
  if (!(weightKg && weightKg > 0) || !(durationMin > 0)) return null;

  if (avgHrBpm && avgHrBpm > 0 && ageYears && sex) {
    const kcal = keytelKcal(avgHrBpm, weightKg, ageYears, sex, durationMin);
    if (kcal != null) {
      return {
        kcal,
        method: "keytel",
        confidence: category.kind === "cardio" ? "high" : "low",
      };
    }
  }

  const kcal = metKcal(category.met, weightKg, durationMin);
  if (kcal == null) return null;
  return { kcal, method: "met", confidence: "low" };
};

// ── Rest-aware strength model (HC-CAL) ────────────────────────────────────────
// A logged strength session is only ~16% of clock time under load — the rest is
// inter-set recovery (verified on real sessions via set timestamps). The old
// strength bout applied a whole-session MET (5.0) — itself a Compendium AVERAGE
// that already blends work + rest — OR a Keytel rate from the session's mildly-
// elevated mean HR, across the ENTIRE session, overcounting ~2× vs a watch. This
// model prices each set as an ELEVATED WINDOW (the lift + its recovery tail —
// where most resistance energy sits: VO2 peaks 35–45 s AFTER a set) and the
// genuine idle remainder near resting. Proximity to failure (effective RIR, so
// a failed set counts maximal via effortScale.effectiveRir at the caller) scales
// the tail length and the active MET. These active METs are WORK-PHASE values,
// deliberately higher than a Compendium session-average would be. Constants calibrated on real sessions
// vs Zepp (r≈0.86); Zepp is a proxy, not truth, so treat them as v1.
export const REST_MET = 1.3; // idle between elevated windows (quiet standing-ish)
const STRENGTH_TAIL_BASE_SEC = 20; // recovery-tail floor, every set
const STRENGTH_TAIL_SPAN_SEC = 70; // extra tail at failure (effort 1)
const STRENGTH_ACTIVE_MET_BASE = 6.0; // dynamic set's elevated window
const STRENGTH_ACTIVE_MET_SPAN = 2.0; // +span at failure → 6..8
const STRENGTH_HOLD_MET = 4.0; // timed/isometric hold (static effort)
// Effective RIR at/above which a set adds no extra recovery cost (fully
// submaximal). Distinct from effortScale's EFFORT_SUBMAXIMAL_RIR (2, the hard-set
// cutoff): here 4 is the point past which more reps-in-reserve stops changing the
// metabolic tail, a calorie-model parameter, not the app-wide hard-set threshold.
const STRENGTH_EFFORT_FLOOR_RIR = 4;
// Effort assumed when a set logged no RIR and no fail (legacy/imported data —
// RIR is now mandatory in the logger). Moderate-hard: an untagged set is real
// work, not free.
const STRENGTH_EFFORT_DEFAULT = 0.6;

/**
 * Map an EFFECTIVE RIR (fail already resolved to 0 upstream by
 * effortScale.effectiveRir) to a 0..1 effort: 0 = easy (RIR ≥ 4), 1 = failure.
 * `null` (no fail, no RIR logged) ⇒ STRENGTH_EFFORT_DEFAULT.
 */
export const strengthEffort = (effectiveRir: number | null): number => {
  if (effectiveRir == null) return STRENGTH_EFFORT_DEFAULT;
  const capped = Math.max(0, Math.min(STRENGTH_EFFORT_FLOOR_RIR, effectiveRir));
  return (STRENGTH_EFFORT_FLOOR_RIR - capped) / STRENGTH_EFFORT_FLOOR_RIR;
};

// One strength set priced as a no-HR MET bout: its elevated window (work + tail)
// at an effort-scaled active MET. Returned as a MetCategory + minutes so the
// caller builds a CalorieBout and prices it through estimateBoutCalories (one
// pricer, no parallel path). `isHold` = a timed/isometric hold (static MET).
export type StrengthWindow = { durationMin: number; category: MetCategory };
export const strengthSetWindow = (
  workSec: number,
  effectiveRir: number | null,
  isHold: boolean,
): StrengthWindow => {
  const effort = strengthEffort(effectiveRir);
  const tailSec = STRENGTH_TAIL_BASE_SEC + STRENGTH_TAIL_SPAN_SEC * effort;
  const met = isHold
    ? STRENGTH_HOLD_MET
    : STRENGTH_ACTIVE_MET_BASE + STRENGTH_ACTIVE_MET_SPAN * effort;
  return { durationMin: (workSec + tailSec) / 60, category: { kind: "strength", met } };
};
